import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  Stack,
  Chip,
  Avatar,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Link } from "react-router-dom";
import axios from "../api/axiosInstance";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import CodeIcon from "@mui/icons-material/Code";
import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TerminalIcon from "@mui/icons-material/Terminal";
import PsychologyIcon from "@mui/icons-material/Psychology";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import GroupsIcon from "@mui/icons-material/Groups";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SpeedIcon from "@mui/icons-material/Speed";
import CoursesCarousel from "../components/CoursesCarousel";
import SuccessStories from "../components/SuccessStories";
import WhyChoose from "../components/WhyChoose";
import LearningJourney from "../components/LearningJourney";
import SEO from "../components/SEO";
import { generateWebsiteSchema, generateOrganizationSchema } from "../utils/structuredData";

const TYPEWRITER_TEXT = "Development";

const Home = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // Typewriter effect state
  const [displayedText, setDisplayedText] = useState("");

  // Callback Form State
  const [callbackForm, setCallbackForm] = useState({ fullName: "", email: "", contactNumber: "" });
  const [callbackLoading, setCallbackLoading] = useState(false);
  const [callbackStatus, setCallbackStatus] = useState(null);

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    if (!callbackForm.fullName || !callbackForm.email || !callbackForm.contactNumber) {
      setCallbackStatus({ type: "error", message: "Please fill all fields." });
      return;
    }
    setCallbackLoading(true);
    setCallbackStatus(null);
    try {
      await axios.post("/callback", {
        full_name: callbackForm.fullName,
        email: callbackForm.email,
        contact_number: callbackForm.contactNumber
      });
      setCallbackStatus({ type: "success", message: "Request sent! We'll call you shortly." });
      setCallbackForm({ fullName: "", email: "", contactNumber: "" });
    } catch (err) {
      setCallbackStatus({ type: "error", message: err.response?.data?.detail || "Failed to send request." });
    } finally {
      setCallbackLoading(false);
    }
  };

  useEffect(() => {
    let currentIndex = 0;
    let isDeleting = false;
    let timeoutId;
    const typingSpeed = 150; // milliseconds per character
    const deletingSpeed = 100; // faster when deleting
    const pauseAfterTyping = 2000; // pause after completing the word
    const pauseAfterDeleting = 500; // pause after deleting

    const typeWriter = () => {
      if (!isDeleting && currentIndex < TYPEWRITER_TEXT.length) {
        // Typing forward
        setDisplayedText(TYPEWRITER_TEXT.substring(0, currentIndex + 1));
        currentIndex++;
        timeoutId = setTimeout(typeWriter, typingSpeed);
      } else if (!isDeleting && currentIndex >= TYPEWRITER_TEXT.length) {
        // Finished typing, wait then start deleting
        timeoutId = setTimeout(() => {
          isDeleting = true;
          typeWriter();
        }, pauseAfterTyping);
      } else if (isDeleting && currentIndex > 0) {
        // Deleting backward
        currentIndex--;
        setDisplayedText(TYPEWRITER_TEXT.substring(0, currentIndex));
        timeoutId = setTimeout(typeWriter, deletingSpeed);
      } else if (isDeleting && currentIndex === 0) {
        // Finished deleting, wait then start typing again
        isDeleting = false;
        timeoutId = setTimeout(typeWriter, pauseAfterDeleting);
      }
    };

    // Start the typewriter effect
    typeWriter();

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  const structuredData = [
    generateWebsiteSchema(),
    generateOrganizationSchema(),
  ];

  // Professional color scheme
  const colors = {
    primary: "#e50914",
    secondary: "#6a0dad",
    accent: "#A380FF",
    success: "#00e676",
    text: isDark ? "#ffffff" : "#1a1a1a",
    textSecondary: isDark ? "#b0b0b0" : "#666666",
    bg: isDark ? "#0a0a0a" : "#ffffff",
    bgSecondary: isDark ? "#1a1a1a" : "#f8f9fa",
  };

  const stats = [
    { number: "1000+", label: "Active Learners", icon: <PeopleIcon /> },
    { number: "50+", label: "Lessons", icon: <PlayCircleFilledIcon /> },
    { number: "6", label: "Languages", icon: <CodeIcon /> },
    { number: "100%", label: "Free Access", icon: <CheckCircleIcon /> },
  ];

  return (
    <>
      <SEO
        title="CodingComet - Learn Programming with Interactive Tutorials"
        description="Master web development, Python, JavaScript, FastAPI, and more with hands-on coding tutorials, interactive code playgrounds, and real-world projects. Start your coding journey today!"
        keywords="learn programming, coding tutorials, web development course, Python tutorial, JavaScript tutorial, FastAPI course, code playground, online compiler, programming education"
        url="/"
        structuredData={structuredData}
      />
      <Box
        sx={{
          color: colors.text,
          minHeight: "100vh",
          background: isDark
            ? "linear-gradient(180deg, #0a0a0a 0%, #1a0a1a 50%, #0a0a0a 100%)"
            : "linear-gradient(180deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%)",
          pt: { xs: 4, sm: 6, md: 8 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* 🕸️ Animated Background Pattern */}
        <Box
          className="coder-bg"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 0,
            pointerEvents: "none",
          }}
        />
        {/* 🟣 Ambient Background Blobs */}
        <Box
          sx={{
            position: "absolute",
            top: "5%",
            left: "0%",
            width: "55vw",
            height: "55vw",
            background: `radial-gradient(circle, ${colors.primary}25 0%, transparent 70%)`,
            filter: "blur(90px)",
            zIndex: 0,
            animation: "pulse-glow 10s infinite alternate",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "0%",
            right: "0%",
            width: "50vw",
            height: "50vw",
            background: `radial-gradient(circle, ${colors.secondary}25 0%, transparent 70%)`,
            filter: "blur(90px)",
            zIndex: 0,
            animation: "pulse-glow 8s infinite alternate-reverse",
          }}
        />
        {/* ... Hero Section ... */}
        <Container
          maxWidth={false}
          sx={{
            py: { xs: 8, md: 8 },
            px: { xs: 2, md: 6 },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Grid
            container
            spacing={4}
            alignItems="center"
            justifyContent="space-around"
          >
            {/* ================= LEFT SECTION (50%) ================= */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                textAlign: { xs: "center", md: "left" },
              }}
            >
              <Box sx={{ mb: 3, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Chip
                  label="🚀 Learn. Build. Master."
                  sx={{
                    background: `linear-gradient(90deg, ${colors.primary}, ${colors.secondary})`,
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.85rem",
                    height: 32,
                    px: 1.5,
                    boxShadow: `0 4px 14px ${colors.primary}40`,
                    letterSpacing: 1
                  }}
                />
              </Box>
              {/* Tech Badges */}
              <Stack
                direction="row"
                spacing={1.5}
                justifyContent={{ xs: "center", md: "flex-start" }}
                sx={{ mb: 3 }}
              >
                {[
                  { label: "FastAPI", color: "#009485" },
                  { label: "React", color: "#61dafb" },
                  { label: "Python", color: "#FFD43B" },
                  { label: "Agentic AI", color: colors.secondary },
                ].map((tech, i) => (
                  <Box
                    key={i}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "50px",
                      border: `1px solid ${tech.color}40`,
                      background: `${tech.color}10`,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background: tech.color,
                      }}
                    />
                    {tech.label}
                  </Box>
                ))}
              </Stack>

              {/* Heading */}
              <Typography
                variant="h2"
                fontWeight={900}
                sx={{
                  mb: 2,
                  fontSize: { xs: "2.5rem", sm: "3rem", md: "3.8rem", lg: "4.2rem" },
                  lineHeight: 1.15,
                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Master Full Stack <br />
                <Box
                  component="span"
                  sx={{
                    borderRight: `4px solid ${colors.primary}`,
                    pr: 1,
                    animation: "blink-caret .75s step-end infinite",
                  }}
                >
                  {displayedText}
                </Box>
              </Typography>

              {/* Subtitle */}
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  maxWidth: 600,
                  mx: { xs: "auto", md: 0 },
                  color: colors.textSecondary,
                  lineHeight: 1.6,
                }}
              >
                Launch your career with expert-led courses in{" "}
                <Box component="span" sx={{ color: colors.primary, fontWeight: 700 }}>
                  FastAPI
                </Box>
                ,{" "}
                <Box component="span" sx={{ color: colors.secondary, fontWeight: 700 }}>
                  React
                </Box>{" "}
                &{" "}
                <Box component="span" sx={{ color: colors.accent, fontWeight: 700 }}>
                  Agentic AI
                </Box>
                . Build real-world projects that matter.
              </Typography>

              {/* CTA Buttons */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent={{ xs: "center", md: "flex-start" }}
                sx={{ mb: 6 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/fastapi"
                  startIcon={<RocketLaunchIcon />}
                  sx={{
                    minWidth: 220,
                    borderRadius: "50px",
                    fontWeight: 700,
                    px: 4,
                    py: 1.8,
                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                    boxShadow: `0 12px 30px ${colors.primary}40`,
                    "&:hover": {
                      background: `linear-gradient(135deg, ${colors.secondary}, ${colors.primary})`,
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Start Learning Free
                </Button>

                <Button
                  variant="text"
                  size="large"
                  component={Link}
                  to="/practice"
                  startIcon={<CodeIcon />}
                  sx={{
                    minWidth: 200,
                    fontWeight: 600,
                    borderRadius: "50px",
                  }}
                >
                  Code Playground
                </Button>
              </Stack>

              {/* Trust Stats */}
              <Stack
                direction="row"
                spacing={4}
                justifyContent={{ xs: "center", md: "flex-start" }}
                sx={{
                  borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                  pt: 4,
                  mt: 2
                }}
              >
                {[
                  { label: "10,000+ Learners", icon: <GroupsIcon sx={{ fontSize: 18, color: colors.primary }} /> },
                  { label: "Hands-on Projects", icon: <CodeIcon sx={{ fontSize: 18, color: colors.secondary }} /> },
                  { label: "Industry Ready", icon: <EmojiEventsIcon sx={{ fontSize: 18, color: colors.accent }} /> },
                ].map((stat, i) => (
                  <Stack key={i} direction="row" alignItems="center" spacing={1}>
                    {stat.icon}
                    <Typography fontWeight={700} fontSize="0.85rem" sx={{ color: colors.text }}>
                      {stat.label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>

            {/* ================= RIGHT SECTION (50% – WIDER) ================= */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: { xs: "100%", md: 480 },
                  position: "relative"
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                  }}
                >
                  {/* Terminal Window Frame */}
                  <Box
                    sx={{
                      position: "absolute",
                      inset: -20,
                      borderRadius: "20px",
                      background: isDark ? "rgba(20, 20, 30, 0.8)" : "rgba(255, 255, 255, 0.8)",
                      backdropFilter: "blur(20px)",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                      boxShadow: `0 20px 60px -10px ${colors.primary}30`,
                      zIndex: 0,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Fake Terminal Header */}
                    <Box sx={{
                      p: 1.5,
                      borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                      display: "flex",
                      gap: 1
                    }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ff5f56' }} />
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ffbd2e' }} />
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#27c93f' }} />
                    </Box>
                    {/* Code Background Chars */}
                    <Box sx={{
                      p: 2,
                      flex: 1,
                      overflow: 'hidden',
                      opacity: 0.1,
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      color: colors.text,
                      userSelect: 'none'
                    }}>
                      &lt;CodingComet&gt;<br />
                      &nbsp;&nbsp;import Future from 'react';<br />
                      &nbsp;&nbsp;const Developer = () =&gt; &#123;<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;return &lt;Master /&gt;<br />
                      &nbsp;&nbsp;&#125;<br />
                      &lt;/CodingComet&gt;
                    </Box>
                  </Box>

                  {/* Registration / Callback Form Container */}
                  <Paper
                    elevation={0}
                    sx={{
                      position: "relative",
                      width: "100%",
                      maxWidth: { xs: "100%", md: 480 },
                      mx: "auto",
                      p: { xs: 3, sm: 4 },
                      borderRadius: "32px",
                      background: isDark
                        ? "linear-gradient(135deg, rgba(30, 30, 40, 0.9) 0%, rgba(20, 20, 30, 0.8) 100%)"
                        : "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(245, 245, 250, 0.8) 100%)",
                      backdropFilter: "blur(20px)",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.5)"}`,
                      boxShadow: `0 40px 100px -20px ${colors.primary}20`,
                      zIndex: 1,
                      overflow: 'hidden'
                    }}
                  >
                    {/* Decorative Elements */}
                    <Box sx={{
                      position: 'absolute',
                      top: '-50px',
                      right: '-50px',
                      width: 150,
                      height: 150,
                      background: `radial-gradient(circle, ${colors.primary}20 0%, transparent 70%)`,
                      zIndex: 0
                    }} />

                    <Box component="form" onSubmit={handleCallbackSubmit} sx={{ position: 'relative', zIndex: 1 }}>
                      <Typography variant="h5" fontWeight={900} sx={{ mb: 1, color: colors.primary }}>
                        Start Your Journey
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: colors.textSecondary }}>
                        Enter your details to get instant access to free resources or request a callback from our mentors.
                      </Typography>

                      <Stack spacing={2.5}>
                        {callbackStatus && (
                          <Alert severity={callbackStatus.type} sx={{ borderRadius: '12px' }}>
                            {callbackStatus.message}
                          </Alert>
                        )}
                        <Box>
                          <Typography variant="caption" fontWeight={700} sx={{ ml: 1, mb: 0.5, display: 'block', color: colors.textSecondary }}>FULL NAME</Typography>
                          <Box component="input"
                            value={callbackForm.fullName}
                            onChange={(e) => setCallbackForm({ ...callbackForm, fullName: e.target.value })}
                            placeholder="John Doe" sx={{
                              width: '100%', p: 1.8, borderRadius: '14px', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                              bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', color: colors.text,
                              outline: 'none', '&:focus': { borderColor: colors.primary, boxShadow: `0 0 0 4px ${colors.primary}15` },
                              transition: 'all 0.3s'
                            }} />
                        </Box>

                        <Box>
                          <Typography variant="caption" fontWeight={700} sx={{ ml: 1, mb: 0.5, display: 'block', color: colors.textSecondary }}>EMAIL ADDRESS</Typography>
                          <Box component="input"
                            type="email"
                            value={callbackForm.email}
                            onChange={(e) => setCallbackForm({ ...callbackForm, email: e.target.value })}
                            placeholder="john@example.com" sx={{
                              width: '100%', p: 1.8, borderRadius: '14px', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                              bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', color: colors.text,
                              outline: 'none', '&:focus': { borderColor: colors.primary, boxShadow: `0 0 0 4px ${colors.primary}15` },
                              transition: 'all 0.3s'
                            }} />
                        </Box>

                        <Box>
                          <Typography variant="caption" fontWeight={700} sx={{ ml: 1, mb: 0.5, display: 'block', color: colors.textSecondary }}>CONTACT NUMBER</Typography>
                          <Box component="input"
                            value={callbackForm.contactNumber}
                            onChange={(e) => setCallbackForm({ ...callbackForm, contactNumber: e.target.value })}
                            placeholder="+91 98765 43210" sx={{
                              width: '100%', p: 1.8, borderRadius: '14px', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                              bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', color: colors.text,
                              outline: 'none', '&:focus': { borderColor: colors.primary, boxShadow: `0 0 0 4px ${colors.primary}15` },
                              transition: 'all 0.3s'
                            }} />
                        </Box>

                        <Button
                          type="submit"
                          variant="contained"
                          fullWidth
                          size="large"
                          disabled={callbackLoading}
                          sx={{
                            mt: 1,
                            py: 2,
                            borderRadius: '16px',
                            fontWeight: 800,
                            fontSize: '1rem',
                            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                            boxShadow: `0 10px 30px ${colors.primary}40`,
                            '&:hover': { transform: 'translateY(-2px)' },
                            transition: 'all 0.3s'
                          }}
                        >
                          {callbackLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : "Request Callback"}
                        </Button>

                        <Typography variant="caption" align="center" sx={{ color: colors.textSecondary }}>
                          By registering, you agree to our <Box component="span" sx={{ color: colors.primary, cursor: 'pointer' }}>Terms & Privacy</Box>
                        </Typography>
                      </Stack>
                    </Box>

                    {/* Badge */}
                    <Box sx={{
                      position: 'absolute',
                      top: 20,
                      right: 20,
                      zIndex: 2
                    }}>
                      <Chip
                        label="FREE"
                        size="small"
                        sx={{
                          bgcolor: colors.success,
                          color: '#000',
                          fontWeight: 900,
                          fontSize: '0.65rem',
                          height: 20
                        }}
                      />
                    </Box>
                  </Paper>

                  {/* Floating Tech Icons - Updated */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-5%",
                      right: "-15%",
                      width: 70,
                      height: 70,
                      borderRadius: "16px",
                      background: isDark ? "#1a1a1a" : "#ffffff",
                      boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      animation: "float 6s ease-in-out infinite",
                      zIndex: 2,
                      border: `1px solid ${colors.primary}40`,
                    }}
                  >
                    <CodeIcon sx={{ fontSize: 36, color: colors.primary }} />
                  </Box>

                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "10%",
                      left: "-8%",
                      width: 60,
                      height: 60,
                      borderRadius: "14px",
                      background: isDark ? "#1a1a1a" : "#ffffff",
                      boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      animation: "float 7s ease-in-out infinite 1s",
                      zIndex: 2,
                      border: `1px solid ${colors.secondary}40`,
                    }}
                  >
                    <TerminalIcon sx={{ fontSize: 32, color: colors.secondary }} />
                  </Box>

                  {/* New Floating Element: Success Badge */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "20%",
                      right: "-5%",
                      px: 2,
                      py: 1,
                      borderRadius: "12px",
                      background: isDark ? "#1a1a1a" : "#ffffff",
                      boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      animation: "float 8s ease-in-out infinite 2s",
                      zIndex: 2,
                      border: `1px solid ${colors.success}40`,
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 20, color: colors.success }} />
                    {/* <Typography variant="caption" fontWeight={700}>Compiled</Typography> */}
                  </Box>

                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>


        {/* 📊 Statistics Section */}
        <Box
          sx={{
            py: { xs: 6, md: 2 },
            background: isDark
              ? "linear-gradient(135deg, #1a0a1a 0%, #0a0a0a 100%)"
              : "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
          }}
        >
          <Container maxWidth="lg">
            <Grid
              container
              spacing={{ xs: 2, sm: 3, md: 4 }}
              justifyContent="center"
              alignItems="stretch"
            >
              {stats.map((stat, index) => (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={3}
                  key={index}
                  sx={{
                    display: "flex",
                  }}
                >
                  <Paper
                    className="glass-card"
                    elevation={0}
                    sx={{
                      p: { xs: 2, sm: 2.5, md: 3 },
                      textAlign: "center",
                      borderRadius: "16px",
                      border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)"}`,
                      transition: "all 0.3s ease",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      animation: `fadeInUp 0.6s ease-out ${index * 0.1}s backwards`,
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: `0 8px 24px ${colors.primary}20`,
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: `${colors.primary}20`,
                        color: colors.primary,
                        width: { xs: 48, sm: 52, md: 56 },
                        height: { xs: 48, sm: 52, md: 56 },
                        mx: "auto",
                        mb: { xs: 1.5, md: 2 },
                      }}
                    >
                      {stat.icon}
                    </Avatar>
                    <Typography
                      variant="h4"
                      fontWeight={900}
                      sx={{
                        mb: 0.5,
                        fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
                        background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {stat.number}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={colors.textSecondary}
                      fontWeight={500}
                      sx={{ fontSize: { xs: "0.8rem", sm: "0.85rem", md: "0.9rem" } }}
                    >
                      {stat.label}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* ✨ Features Section Redesigned */}
        <WhyChoose colors={colors} isDark={isDark} />

        {/* 🗣️ Testimonials Section NEW */}
        <SuccessStories colors={colors} isDark={isDark} />

        {/* 🎓 Courses Carousel */}
        <Box
          sx={{
            py: { xs: 6, md: 4 },
            background: isDark
              ? "linear-gradient(135deg, #0a0a0a 0%, #1a0a1a 100%)"
              : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          }}
        >

          <Container maxWidth="lg">
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h3"
                fontWeight={900}
                sx={{
                  mb: 2,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                  background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Explore Our Courses
              </Typography>
              <Typography variant="h6" color={colors.textSecondary} sx={{ maxWidth: 600, mx: "auto" }}>
                Comprehensive learning paths designed for developers of all levels
              </Typography>
            </Box>
            <CoursesCarousel />
          </Container>
        </Box>

        {/* 🎯 Learning Path Section Redesigned */}
        <LearningJourney colors={colors} isDark={isDark} />

        {/* 🏆 Why Learn With Us Section */}
        <Box
          sx={{
            py: { xs: 8, md: 2 },
            background: isDark
              ? "linear-gradient(135deg, #1a0a1a 0%, #0a0a0a 100%)"
              : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          }}
        >
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center" justifyContent="space-around">
              <Grid size={{ xs: 12, md: 5 }}>
                <Typography
                  variant="h3"
                  fontWeight={900}
                  sx={{
                    mb: 3,
                    fontSize: { xs: "2rem", md: "2.5rem" },
                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Why Choose CodingComet?
                </Typography>
                <Stack spacing={3}>
                  {[
                    { icon: <SpeedIcon />, title: "Fast Learning", desc: "Concise, focused lessons that get you coding quickly" },
                    { icon: <PsychologyIcon />, title: "Hands-On Practice", desc: "Interactive code playgrounds for every concept" },
                    { icon: <WorkspacePremiumIcon />, title: "Industry Standards", desc: "Learn best practices used by top companies" },
                    { icon: <GroupsIcon />, title: "Community Support", desc: "Join a growing community of learners and developers" },
                  ].map((item, index) => (
                    <Box key={index} sx={{ display: "flex", gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: `${colors.primary}20`,
                          color: colors.primary,
                          width: 56,
                          height: 56,
                        }}
                      >
                        {item.icon}
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color={colors.textSecondary}>
                          {item.desc}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Paper
                  elevation={8}
                  sx={{
                    p: 4,
                    background: isDark
                      ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
                      : "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                    borderRadius: "24px",
                    border: `2px solid ${colors.primary}30`,
                  }}
                >
                  <Stack spacing={3}>
                    {[
                      { label: "Course Completion Rate", value: "94%", color: colors.primary },
                      { label: "Student Satisfaction", value: "97%", color: colors.secondary },
                      { label: "Job Placement", value: "85%", color: colors.accent },
                      { label: "Free Forever", value: "100%", color: colors.success },
                    ].map((stat, index) => (
                      <Box key={index}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="body1" fontWeight={600}>
                            {stat.label}
                          </Typography>
                          <Typography variant="h6" fontWeight={900} sx={{ color: stat.color }}>
                            {stat.value}
                          </Typography>
                        </Stack>
                        <Box
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            background: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${parseInt(stat.value)}%`,
                              background: `linear-gradient(90deg, ${stat.color}, ${stat.color}dd)`,
                              borderRadius: 4,
                              transition: "width 1s ease",
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* 🚀 Call to Action Section */}
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Container maxWidth="md">
            <Box sx={{ textAlign: "center", position: "relative", zIndex: 1 }}>
              <Typography
                variant="h3"
                fontWeight={900}
                sx={{
                  mb: 3,
                  color: "#fff",
                  fontSize: { xs: "2rem", md: "3rem" },
                }}
              >
                Ready to Start Your Journey?
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 4,
                  color: "rgba(255,255,255,0.9)",
                  maxWidth: 600,
                  mx: "auto",
                  lineHeight: 1.7,
                }}
              >
                Join thousands of developers learning modern web development.
                Start coding today—it's completely free!
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/fastapi"
                  startIcon={<RocketLaunchIcon />}
                  sx={{
                    minWidth: 220,
                    background: "#fff",
                    color: colors.primary,
                    fontWeight: 700,
                    borderRadius: "12px",
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "#f5f5f5",
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 32px rgba(0,0,0,0.3)",
                    },
                  }}
                >
                  Browse Courses
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/practice"
                  sx={{
                    minWidth: 220,
                    borderColor: "#fff",
                    color: "#fff",
                    borderWidth: 2,
                    fontWeight: 700,
                    borderRadius: "12px",
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      background: "rgba(255,255,255,0.1)",
                      borderColor: "#fff",
                      borderWidth: 2,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  Try Playground
                </Button>
              </Stack>
            </Box>
          </Container>
          {/* Decorative background elements */}
          <Box
            sx={{
              position: "absolute",
              top: -100,
              right: -100,
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.1)",
              zIndex: 0,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -80,
              left: -80,
              width: 250,
              height: 250,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.1)",
              zIndex: 0,
            }}
          />
        </Box>
      </Box>
    </>
  );
}

export default Home
